"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { CheckCircle, XCircle, AlertTriangle, Info, FileText, X } from "lucide-react";
import { Button } from "../../common";
import "./AlertModal.css";

type AlertType = "success" | "error" | "warning" | "info" | "detail";

interface AlertModalProps {
  type?: AlertType;
  title?: string;
  children: React.ReactNode;
  className?: string;
  closable?: boolean;
  onClose?: () => void;
  autoCloseDuration?: number; // ms
  showTimer?: boolean;        // نمایش شمارش معکوس
  primaryActionLabel?: string;
  onPrimaryAction?: () => void;
  secondaryActionLabel?: string;
  onSecondaryAction?: () => void;
  preventBackdropClose?: boolean; // کلیک روی بک‌دراپ نبندد
}

const IconByType: Record<AlertType, React.ReactNode> = {
  success: <CheckCircle className="alertmodal-icon-svg icon-success" aria-hidden="true" />,
  error:   <XCircle className="alertmodal-icon-svg icon-error" aria-hidden="true" />,
  warning: <AlertTriangle className="alertmodal-icon-svg icon-warning" aria-hidden="true" />,
  info:    <Info className="alertmodal-icon-svg icon-info" aria-hidden="true" />,
  detail:  <FileText className="alertmodal-icon-svg icon-detail" aria-hidden="true" />,
};

const AlertModal: React.FC<AlertModalProps> = ({
  type = "info",
  title,
  children,
  className = "",
  closable = true,
  onClose,
  autoCloseDuration,
  showTimer = false,
  primaryActionLabel,
  onPrimaryAction,
  secondaryActionLabel,
  onSecondaryAction,
  preventBackdropClose = false,
}) => {
  const dialogRef = useRef<HTMLDivElement | null>(null);

  // تایمر و پروگرس
  const totalMs = autoCloseDuration ?? 0;
  const totalSec = useMemo(() => Math.ceil(totalMs / 1000), [totalMs]);
  const [leftSec, setLeftSec] = useState(totalSec);

  useEffect(() => {
    if (!totalMs || !onClose) return;
    setLeftSec(totalSec);

    const startedAt = Date.now();
    const tick = setInterval(() => {
      const elapsed = Date.now() - startedAt;
      const remain = Math.max(0, totalMs - elapsed);
      setLeftSec(Math.ceil(remain / 1000));
      if (remain <= 0) {
        clearInterval(tick);
        onClose?.();
      }
    }, 250);

    return () => clearInterval(tick);
  }, [totalMs, totalSec, onClose]);

  const progress = totalMs ? (1 - (leftSec * 1000) / totalMs) * 100 : 0;

  // بستن با ESC
  useEffect(() => {
    if (!closable) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [closable, onClose]);

  // فوکوس اولیه روی پنل
  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (preventBackdropClose || !closable) return;
    if (e.target === e.currentTarget) onClose?.();
  };

  return (
    <div
      className={`alertmodal-overlay ${className}`}
      onMouseDown={handleBackdropClick}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="alertmodal-panel"
        data-type={type}
        tabIndex={-1}
        ref={dialogRef}
        aria-live="assertive"
        aria-atomic="true"
      >
        <div className="alertmodal-icon">{IconByType[type]}</div>

        {title && <h2 className="alertmodal-title">{title}</h2>}

        <div className="alertmodal-content">{children}</div>

        {(primaryActionLabel || secondaryActionLabel || closable) && (
          <div className="alertmodal-actions">
            {secondaryActionLabel && (
              <Button
                type="button"
                variant="outline"
                size="md"
                onClick={onSecondaryAction}
                className="alertmodal-btn"
              >
                {secondaryActionLabel}
              </Button>
            )}

            {primaryActionLabel && (
              <Button
                type="button"
                variant={type === "error" ? "danger" : type === "warning" ? "warning" : "primary"}
                size="md"
                onClick={onPrimaryAction}
                className="alertmodal-btn"
              >
                {primaryActionLabel}
              </Button>
            )}

            {closable && (
              <button
                type="button"
                className="alertmodal-close-icon"
                aria-label="Close alert"
                onClick={onClose}
              >
                <X className="close-svg" aria-hidden="true" />
              </button>
            )}
          </div>
        )}

        {autoCloseDuration && showTimer && (
          <div className="alertmodal-timer" role="timer" aria-live="off">
            <div
              className="alertmodal-progress"
              style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
            />
            <span className="alertmodal-countdown">{leftSec}s</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default AlertModal;
